import { useCallback, useEffect, useMemo, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { getJobs, type Job } from '@/lib/api/jobs';

// ── Crew memo board (CR #131) ───────────────────────────────────────────────
// The everyday sibling of toolbox talks: reminders, notices, schedule changes,
// kudos and urgent messages. Optional read-receipts, pinning, expiry dates,
// job tagging, and a comment thread the crew can reply in from their portal.

interface Ack { user_id: number; name: string; acked_at: string }
interface Memo {
  id: number; title: string; body: string;
  category: 'notice' | 'reminder' | 'urgent' | 'kudos' | 'schedule';
  pinned: number; requires_ack: number; archived: number;
  audience: 'crew' | 'clients' | 'both';
  job_id: number | null; job_title: string | null; expires_on: string | null;
  created_by_name?: string; created_at: string;
  ack_count: number; comment_count: number; acks: Ack[]; crew_size: number;
}
interface Comment { id: number; body: string; created_at: string; author: string; role: string }

const CATS: { id: Memo['category']; label: string; icon: string; cls: string }[] = [
  { id: 'notice',   label: 'Notice',   icon: 'campaign',       cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  { id: 'reminder', label: 'Reminder', icon: 'notifications',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'urgent',   label: 'Urgent',   icon: 'priority_high',  cls: 'bg-red-100 text-red-700 border-red-200' },
  { id: 'schedule', label: 'Schedule', icon: 'event',          cls: 'bg-purple-50 text-purple-700 border-purple-200' },
  { id: 'kudos',    label: 'Kudos',    icon: 'celebration',    cls: 'bg-green-100 text-green-700 border-green-200' },
];
const catOf = (c: string) => CATS.find(x => x.id === c) || CATS[0];

const QUICK: { label: string; title: string; body: string; category: Memo['category'] }[] = [
  { label: 'Early start', title: 'Early start tomorrow', body: 'We start at 6:30am tomorrow to beat the heat. Be on site with your gear ready to go.', category: 'schedule' },
  { label: 'Weather call', title: 'Weather call', body: 'Rain in the forecast — check this board by 6am for the go/no-go before driving out.', category: 'urgent' },
  { label: 'Shop cleanup', title: 'Shop / trailer cleanup', body: 'Trailer gets reorganized this week. Pull your personal tools out and label anything you want kept.', category: 'reminder' },
  { label: 'Nice work', title: 'Nice work this week', body: 'Good push from everyone to get the framing closed in ahead of the rain — noticed and appreciated.', category: 'kudos' },
];

const fmtWhen = (s: string) =>
  new Date(s).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export const MemoBoardPage = () => {
  const [memos, setMemos] = useState<Memo[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');

  // composer
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState<Memo['category']>('notice');
  // CR #153 — who this memo is for: the crew, your clients, or both
  const [audience, setAudience] = useState<Memo['audience']>('crew');
  const [requiresAck, setRequiresAck] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [jobId, setJobId] = useState('');
  const [expires, setExpires] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiClient.get(`/crew-memos${showArchived ? '?archived=1' : ''}`)
      .then(r => setMemos(r.data.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getJobs({ limit: 200 }).then(r => setJobs(r.jobs)).catch(() => {}); }, []);

  const post = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      await apiClient.post('/crew-memos', {
        title: title.trim(), body: body.trim(), category, audience,
        requires_ack: requiresAck, pinned, job_id: jobId || null, expires_on: expires || null,
      });
      setTitle(''); setBody(''); setJobId(''); setExpires(''); setRequiresAck(false); setPinned(false); setAudience('crew');
      load();
    } finally { setBusy(false); }
  };

  const patch = async (id: number, b: Record<string, unknown>) => {
    setMemos(ms => ms.map(m => m.id === id ? { ...m, ...b } as Memo : m));
    try { await apiClient.patch(`/crew-memos/${id}`, b); if ('archived' in b) load(); } catch { load(); }
  };
  const remove = async (id: number) => {
    if (!window.confirm('Delete this memo, its read-receipts and comments?')) return;
    setMemos(ms => ms.filter(m => m.id !== id));
    try { await apiClient.delete(`/crew-memos/${id}`); } catch { load(); }
  };
  const openMemo = async (id: number) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id); setComments([]);
    try { const r = await apiClient.get(`/crew-memos/${id}/comments`); setComments(r.data.data ?? []); }
    catch { /* empty */ }
  };
  const addComment = async (id: number) => {
    const text = newComment.trim();
    if (!text) return;
    setNewComment('');
    try {
      await apiClient.post(`/crew-memos/${id}/comments`, { body: text });
      const r = await apiClient.get(`/crew-memos/${id}/comments`);
      setComments(r.data.data ?? []);
      setMemos(ms => ms.map(m => m.id === id ? { ...m, comment_count: (m.comment_count || 0) + 1 } : m));
    } catch { /* ignore */ }
  };

  const visible = useMemo(() => memos.filter(m =>
    (!filter || m.category === filter) &&
    (!search.trim() || (m.title + ' ' + m.body).toLowerCase().includes(search.trim().toLowerCase()))
  ), [memos, filter, search]);

  const awaitingReceipts = memos.filter(m => m.requires_ack && m.ack_count < m.crew_size).length;

  return (
    <MainLayout>
      <div className="space-y-5">
        <LargeTitleHeader
          title="Crew Board"
          subtitle="Reminders, notices and messages for the crew — with optional read-receipts and replies"
        />

        {/* Composer */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2.5">
          <div className="flex gap-1.5 flex-wrap">
            {QUICK.map(q => (
              <button key={q.label} onClick={() => { setTitle(q.title); setBody(q.body); setCategory(q.category); }}
                className="h-7 px-2.5 rounded-full text-[11px] font-semibold border border-gray-200 text-gray-600 hover:border-[#C77D3F]/50 hover:text-[#C77D3F]">
                {q.label}
              </button>
            ))}
          </div>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="What do you need the crew to know?"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3}
            placeholder="The details…"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none" />
          <div className="flex gap-2 flex-wrap items-center text-xs">
            <div className="flex gap-1">
              {CATS.map(c => (
                <button key={c.id} onClick={() => setCategory(c.id)}
                  className={`h-8 px-2.5 rounded-lg border text-[11px] font-bold flex items-center gap-1 ${
                    category === c.id ? c.cls : 'bg-white border-gray-200 text-gray-500'}`}>
                  <span className="material-symbols-outlined text-sm">{c.icon}</span>{c.label}
                </button>
              ))}
            </div>
            {/* CR #153 — audience: crew only, clients only, or both */}
            <div className="flex gap-1 items-center">
              <span className="text-gray-400 font-semibold">To:</span>
              {([['crew', 'Crew'], ['clients', 'Clients'], ['both', 'Both']] as const).map(([id, label]) => (
                <button key={id} onClick={() => setAudience(id)}
                  className={`h-8 px-2.5 rounded-lg border text-[11px] font-bold ${
                    audience === id ? 'bg-[#C77D3F] border-[#C77D3F] text-white' : 'bg-white border-gray-200 text-gray-500'}`}>
                  {label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-gray-600">
              <input type="checkbox" checked={requiresAck} onChange={e => setRequiresAck(e.target.checked)} className="accent-[#C77D3F]" />
              Require read-receipt
            </label>
            <label className="flex items-center gap-1.5 text-gray-600">
              <input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} className="accent-[#C77D3F]" />
              Pin
            </label>
            <select value={jobId} onChange={e => setJobId(e.target.value)}
              className="h-8 px-2 rounded-lg border border-gray-300 bg-white text-gray-700 max-w-[12rem]">
              <option value="">No specific job</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-gray-600">
              Expires
              <input type="date" value={expires} onChange={e => setExpires(e.target.value)}
                className="h-8 px-2 rounded-lg border border-gray-300 text-gray-700" />
            </label>
          </div>
          <button onClick={post} disabled={busy || !title.trim() || !body.trim()}
            className="h-10 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-base">send</span>
            {busy ? 'Posting…' : audience === 'crew' ? 'Post to the crew' : audience === 'clients' ? 'Post to clients' : 'Post to crew & clients'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter('')}
            className={`h-8 px-3 rounded-full text-xs font-bold ${!filter ? 'bg-[#C77D3F] text-white' : 'bg-gray-100 text-gray-600'}`}>All</button>
          {CATS.map(c => (
            <button key={c.id} onClick={() => setFilter(filter === c.id ? '' : c.id)}
              className={`h-8 px-3 rounded-full text-xs font-bold ${filter === c.id ? 'bg-[#C77D3F] text-white' : 'bg-gray-100 text-gray-600'}`}>
              {c.label}
            </button>
          ))}
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            className="h-8 px-3 rounded-lg border border-gray-200 text-sm w-44 focus:outline-none" />
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="accent-[#C77D3F]" />
            Include archived
          </label>
          {awaitingReceipts > 0 && (
            <span className="ml-auto text-xs font-semibold text-amber-700">{awaitingReceipts} memo(s) awaiting read-receipts</span>
          )}
        </div>

        {/* Board */}
        {loading ? (
          <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl block mb-2">forum</span>
            <p className="text-sm text-gray-500">Nothing on the board — post a reminder or notice above.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {visible.map(m => {
              const c = catOf(m.category);
              const allRead = m.requires_ack ? m.ack_count >= m.crew_size : true;
              return (
                <div key={m.id} className={`bg-white rounded-xl border overflow-hidden ${m.category === 'urgent' ? 'border-red-200' : 'border-gray-200'}`}>
                  <button onClick={() => openMemo(m.id)} className="w-full flex items-start gap-2.5 px-4 py-3 text-left">
                    <span className={`material-symbols-outlined text-xl mt-0.5 ${m.category === 'urgent' ? 'text-red-600' : 'text-[#C77D3F]'}`}>{c.icon}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-900 truncate">
                        {!!m.pinned && '📌 '}{m.title}
                      </span>
                      <span className="block text-[11px] text-gray-400">
                        {fmtWhen(m.created_at)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}
                        {m.job_title ? ` · ${m.job_title}` : ''}
                        {m.expires_on ? ` · until ${String(m.expires_on).slice(5, 10)}` : ''}
                        {m.archived ? ' · archived' : ''}
                      </span>
                    </span>
                    {m.audience !== 'crew' && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C77D3F]/10 text-[#8B5A2B] whitespace-nowrap">
                        {m.audience === 'clients' ? 'Clients' : 'Crew + clients'}
                      </span>
                    )}
                    {m.comment_count > 0 && (
                      <span className="text-[11px] text-gray-500 flex items-center gap-0.5">
                        <span className="material-symbols-outlined text-sm">chat_bubble</span>{m.comment_count}
                      </span>
                    )}
                    {!!m.requires_ack && (
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${allRead ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                        {m.ack_count}/{m.crew_size} read
                      </span>
                    )}
                  </button>
                  {open === m.id && (
                    <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.body}</p>

                      {!!m.requires_ack && (
                        <div className="text-xs">
                          <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px] mb-1">Read by</p>
                          {m.acks.length === 0 && <p className="text-gray-400 italic">Nobody yet.</p>}
                          {m.acks.map(a => (
                            <p key={a.user_id} className="text-gray-600">✓ {a.name} — {fmtWhen(a.acked_at)}</p>
                          ))}
                        </div>
                      )}

                      {/* Comment thread */}
                      <div className="bg-gray-50 rounded-lg p-2.5 space-y-1.5">
                        {comments.length === 0 && <p className="text-xs text-gray-400 italic">No replies yet.</p>}
                        {comments.map(cm => (
                          <div key={cm.id} className="text-xs">
                            <span className="font-semibold text-gray-700">{cm.author}</span>
                            <span className="text-gray-400"> · {fmtWhen(cm.created_at)}</span>
                            <p className="text-gray-700">{cm.body}</p>
                          </div>
                        ))}
                        <div className="flex gap-1.5 pt-1">
                          <input value={newComment} onChange={e => setNewComment(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') addComment(m.id); }}
                            placeholder="Reply to the crew…"
                            className="flex-1 h-8 px-2 rounded-lg border border-gray-200 text-xs focus:outline-none" />
                          <button onClick={() => addComment(m.id)}
                            className="h-8 px-3 rounded-lg bg-[#C77D3F] text-white text-xs font-bold">Send</button>
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <button onClick={() => patch(m.id, { pinned: !m.pinned })}
                          className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">
                          {m.pinned ? 'Unpin' : 'Pin'}
                        </button>
                        <button onClick={() => patch(m.id, { archived: !m.archived })}
                          className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">
                          {m.archived ? 'Restore' : 'Archive'}
                        </button>
                        <button onClick={() => remove(m.id)}
                          className="h-8 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-bold ml-auto">Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MainLayout>
  );
};
